import { AnalyticsService } from '../../services/analytics.service';
import { isPlatformBrowser } from '@angular/common';
import {
  CatalogRecord,
  ProductRequestItem,
  initProductRequest,
  ProductRequest,
  initProductRequestItem
} from 'shared';
import { AuthService } from '../../services/auth.service';
import { inject, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { take, mergeMap, map, shareReplay } from 'rxjs/operators';
import { Observable, BehaviorSubject } from 'rxjs';
import { limit, orderBy, serverTimestamp, where } from '@angular/fire/firestore';
import { DbService } from './../../services/db.service';
@Injectable({
  providedIn: 'root'
})
export class ProductRequestService {
  private _db = inject(DbService);

  private productRequest$ = new BehaviorSubject<ProductRequest>(initProductRequest());
  private activeRequest$ = this.productRequest$.asObservable().pipe(shareReplay());
  private activeRequest: ProductRequest = initProductRequest();

  constructor(
    private analyticsService: AnalyticsService,
    private dbService: DbService,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.updateActiveRequest(this.getCachedActiveRequest());
    this.getActiveRequest$().subscribe((request) => {
      this.activeRequest = request;
    });
  }

  getActiveRequest$(): Observable<ProductRequest> {
    return this.activeRequest$;
  }

  updateActiveRequest(productRequest?: ProductRequest): void {
    if (!productRequest) {
      productRequest = this.activeRequest;
    }
    this.productRequest$.next(this.validateProductRequest(productRequest));
    this.setCachedActiveRequest(productRequest);
  }

  async addCatalogItemToActiveRequest(catalogRecord: CatalogRecord): Promise<void> {
    const requestItem = initProductRequestItem({
      id: catalogRecord.id,
      name: catalogRecord.name,
      size: catalogRecord.size
    });
    this.activeRequest.items.push(requestItem);
    await this.analyticsService.logEvent('wholesale_request_product_add_item_catalog');
    this.updateActiveRequest();
  }

  async addItemToActiveRequest(item: ProductRequestItem): Promise<void> {
    this.activeRequest.items.push(item);
    await this.analyticsService.logEvent('wholesale_request_product_add_item_custom');
    this.updateActiveRequest();
  }

  private validateProductRequest(request: ProductRequest): ProductRequest {
    const blank = initProductRequest();
    const existing = Object.keys(request);
    const valid = Object.keys(blank).some((key) => {
      return existing.indexOf(key) > -1;
    });
    return valid ? request : blank;
  }

  private getCachedActiveRequest(): ProductRequest {
    let request = initProductRequest();
    if (isPlatformBrowser(this.platformId)) {
      request = JSON.parse(localStorage.getItem('activeRequest'));
      if (request) {
        Object.keys(request).forEach((key) => {
          if (!Number.isNaN(Date.parse(request[key]))) {
            request[key] = new Date(request[key]);
          }
        });
      }
    }
    return request;
  }

  private setCachedActiveRequest(request: ProductRequest): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('activeRequest', JSON.stringify(request));
    }
  }

  async createProductRequest(request: ProductRequest): Promise<void> {
    const user = await this.authService.getCurrentUser();
    const timestamp = serverTimestamp();
    await this.analyticsService.logEvent('wholesale_request_product', {
      itemCount: request.items.length
    });

    const id = this._db.createId('product-requests');
    return this._db.setDoc('product-requests', id.id, {
      ...request,
      createdAt: timestamp,
      updatedAt: timestamp,
      createdBy: user.uid,
      updatedBy: user.uid,
      recordId: id.id,
      id: id.id
    });
  }

  getPreviousRequests(): Observable<ProductRequest[]> {
    return this.authService.$user.pipe(
      take(1),
      mergeMap((user) => {
        return this.dbService
          .queryCollection$<ProductRequest>('product-requests', [
            where('createdBy', '==', user.uid),
            orderBy('updatedAt', 'desc'),
            limit(20)
          ])
          .pipe(
            map((records) => {
              return records.map((rec: any) => {
                return {
                  ...rec,
                  createdAt: rec.createdAt.toDate(),
                  updatedAt: rec.createdAt.toDate(),
                  dateNeeded: rec.createdAt.toDate()
                };
              });
            })
          );
      })
    );
  }
}
